import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";

export default function Hero() {
  const containerRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 25, damping: 30 });
  const springY = useSpring(mouseY, { stiffness: 25, damping: 30 });

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const contentOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 0.4], [0, 80]);
  const orbScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.2]);
  const orbOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 30;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 30;
    mouseX.set(x);
    mouseY.set(y);
  };

  const scrollTo = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const easeOut = [0.22, 1, 0.36, 1];

  return (
    <section
      id="hero"
      ref={containerRef}
      onMouseMove={handleMouseMove}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-20"
    >
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ opacity: 0.3, mixBlendMode: "lighten" }}
      >
        <source src="https://customer-assets.emergentagent.com/job_deva-command-nexus/artifacts/3soqlx7r_VID-20260418-WA0000.mp4" type="video/mp4" />
      </video>

      {/* Orb - Central Glowing Core */}
      <motion.div
        style={{ x: springX, y: springY, scale: orbScale, opacity: orbOpacity }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
      >
        {/* Outer ambient glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] md:w-[700px] md:h-[700px] orb-outer rounded-full animate-pulse-slow" />

        {/* Inner glow field */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[250px] h-[250px] md:w-[450px] md:h-[450px] orb-inner rounded-full animate-pulse-slow"
          style={{ animationDelay: "-2s" }}
        />

        {/* SVG Ring System - hidden on small mobile */}
        <svg
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] md:w-[550px] md:h-[550px] animate-rotate-slow hidden sm:block"
          viewBox="0 0 600 600"
          fill="none"
        >
          <circle cx="300" cy="300" r="260" stroke="rgba(0,229,255,0.06)" strokeWidth="0.5" strokeDasharray="12 28" />
          <circle cx="300" cy="300" r="210" stroke="rgba(122,92,255,0.05)" strokeWidth="0.5" strokeDasharray="8 22" />
          <circle cx="300" cy="300" r="160" stroke="rgba(255,45,154,0.04)" strokeWidth="0.5" strokeDasharray="4 18" />
        </svg>

        {/* Counter-rotating rings - hidden on small mobile */}
        <svg
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] md:w-[550px] md:h-[550px] animate-rotate-reverse hidden sm:block"
          viewBox="0 0 600 600"
          fill="none"
        >
          <circle cx="300" cy="300" r="240" stroke="rgba(0,229,255,0.04)" strokeWidth="0.5" strokeDasharray="6 32" />
          <circle cx="300" cy="300" r="180" stroke="rgba(122,92,255,0.03)" strokeWidth="0.5" strokeDasharray="3 24" />
        </svg>

        {/* Core Point */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#00E5FF]"
          style={{
            boxShadow:
              "0 0 40px 12px rgba(0, 229, 255, 0.25), 0 0 100px 30px rgba(0, 229, 255, 0.08)",
          }}
        />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 text-center px-6 max-w-4xl"
      >
        {/* System Label */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5, delay: 0.3 }}
          className="sys-label mb-6 md:mb-14"
        >
          SYS.INIT //
        </motion.p>

        {/* Headline - each word loads one after another */}
        <h1 className="font-heading text-[2.75rem] sm:text-6xl lg:text-7xl font-extralight tracking-tighter leading-[1.1]">
          <motion.span
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: easeOut }}
            className="block text-white pop-click"
          >
            Design.
          </motion.span>
          <motion.span
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.8, ease: easeOut }}
            className="block text-transparent bg-clip-text pop-click"
            style={{ backgroundImage: "linear-gradient(135deg, #C49B3C, #F5D77A, #A67C28)" }}
          >
            Engineering.
          </motion.span>
          <motion.span
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 1.1, ease: easeOut }}
            className="block bg-gradient-to-r from-[#00E5FF] to-[#7A5CFF] bg-clip-text text-transparent pop-click"
          >
            Velocity.
          </motion.span>
          <motion.span
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 1.4, ease: easeOut }}
            className="block bg-gradient-to-r from-[#7A5CFF] to-[#FF2D9A] bg-clip-text text-transparent pop-click"
          >
            AI.
          </motion.span>
        </h1>

        {/* Subtext */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 1.8, ease: easeOut }}
          className="hero-subtext pop-click mt-6 md:mt-10 text-sm md:text-lg max-w-lg mx-auto font-body leading-relaxed font-light"
        >
          Crafted for brands entering the <span className="hero-subtext-accent">future</span>.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 2.1, ease: easeOut }}
          className="mt-8 md:mt-18 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6"
        >
          <button
            data-testid="hero-explore-btn"
            onClick={() => scrollTo("#deva")}
            className="group relative px-10 py-4 border border-[#00E5FF]/25 text-[#00E5FF] font-mono text-[10px] tracking-[0.25em] uppercase overflow-hidden transition-all duration-700 hover:border-[#00E5FF]/60 hover:shadow-[0_0_30px_rgba(0,229,255,0.1)]"
          >
            <span className="relative z-10">Explore</span>
            <div className="absolute inset-0 bg-[#00E5FF]/[0.04] translate-y-full group-hover:translate-y-0 transition-transform duration-700 ease-out" />
          </button>
          <button
            data-testid="hero-book-call-btn"
            onClick={() => scrollTo("#contact")}
            className="px-10 py-4 bg-white/[0.02] border border-white/[0.08] text-white/70 font-mono text-[10px] tracking-[0.25em] uppercase transition-all duration-700 hover:bg-white/[0.04] hover:border-white/15 hover:text-white"
          >
            Book Call
          </button>
        </motion.div>

        {/* Scroll Indicator - inside content flow */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5, delay: 2.8 }}
          className="mt-8 flex flex-col items-center gap-3"
        >
          <span className="font-mono text-[8px] tracking-[0.3em] uppercase text-zinc-700">
            Scroll
          </span>
          <ChevronDown size={12} className="text-zinc-700 scroll-indicator" />
        </motion.div>
      </motion.div>
    </section>
  );
}
